'use client';

import { useRef, useMemo, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { AlertCircle, Bike, Car, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input/input';
import { PasswordInput } from '@/components/ui/input/password-input';
import { Label } from '@/components/ui/label/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { useTenantSlug } from '@/hooks/use-tenant-slug';

import { type UseRegistrationAuthResult } from '../../hooks/useRegistrationAuth';
import { identify } from '../../lib/analytics';
import { trackRego } from '../../lib/registration-analytics';
import { useCountries } from '../../hooks/use-public-lookups';
import { useCampaignDefaultMake } from '../../hooks/useCampaignDefaultMake';
import {
  createEmailCheckSchema,
  createSignupSchema,
  createLoginSchema,
  type EmailCheckFormData,
  type SignupFormData,
  type LoginFormData,
} from '../../schemas/registration';
import { CompleteRegistrationStep } from './CompleteRegistrationStep';
import { VehicleFields } from './VehicleFields';
import { StepFormActions } from './StepFormActions';
import {
  REGISTRATION_PHONE_VARIANT,
  REGISTRATION_THEME_CLASSES,
  type RegistrationTheme,
} from './theme';
import { useDeviceCapabilities } from '@/hooks/use-device-capabilities';
import { useScrollIntoViewOnReveal } from '@/hooks/use-scroll-into-view-on-reveal';

/**
 * Confirmed email badge with "Change" action
 */
function EmailBadge({
  email,
  onChangeEmail,
  changeLabel,
  theme = 'dark',
}: {
  email: string;
  onChangeEmail: () => void;
  changeLabel: string;
  theme?: RegistrationTheme;
}) {
  const c = REGISTRATION_THEME_CLASSES[theme];
  return (
    <div className={c.emailBadge}>
      <CheckCircle2 className={c.emailBadgeIcon} aria-hidden="true" />
      <span className={c.emailBadgeText}>{email}</span>
      <button type="button" onClick={onChangeEmail} className={c.emailBadgeChange}>
        {changeLabel}
      </button>
    </div>
  );
}

interface AuthStepProps {
  /** Called when auth succeeds - typically advances to next step */
  onSuccess: () => void;
  /** Called when back button is clicked - typically returns to previous step */
  onBack?: () => void;
  /** City name to display (e.g., "Sydney") */
  cityName?: string;
  /** Event ID for the selected ride */
  eventId: string;
  /** Whether to join the waitlist if the event is at capacity */
  joinWaitlist: boolean;
  /** Country code for phone input default */
  countryCode?: string;
  /** Auth hook result — passed from parent to share state */
  auth: UseRegistrationAuthResult;
  authenticatedProfile?: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  } | null;
  /** Vehicle type for signup forms. Defaults to tenant-slug derivation (dgd → car, else motorcycle). */
  vehicleType?: 'motorcycle' | 'car';
  /** Additional className for the container */
  className?: string;
  /**
   * Visual theme. 'dark' (default) keeps the original stone/dgr-gold literals
   * used by the DGR/DGD register pages; 'semantic' uses shadcn tokens so the
   * [data-tenant] CSS scope controls the palette (registration engine).
   */
  theme?: RegistrationTheme;
}

/**
 * AuthStep - Email-first adaptive auth for registration flow
 *
 * Phase 1: User enters email → server checks if account exists
 * Phase 2a: New user → signup form (name, phone, password, vehicle)
 * Phase 2b: Existing user → login form (password only)
 * Phase 2c: Already registered → info message + dashboard link
 *
 * @example
 * ```tsx
 * <AuthStep
 *   onSuccess={() => setCurrentStep(3)}
 *   cityName="Sydney"
 * />
 * ```
 */
export function AuthStep({
  onSuccess,
  onBack,
  cityName,
  eventId,
  joinWaitlist,
  countryCode,
  auth,
  authenticatedProfile = null,
  vehicleType: vehicleTypeProp,
  className = '',
  theme = 'dark',
}: AuthStepProps) {
  const c = REGISTRATION_THEME_CLASSES[theme];
  const tAuth = useTranslations('auth');
  const tCommon = useTranslations('common');
  const tRegister = useTranslations('register');
  const authErrorRef = useRef<HTMLDivElement>(null);
  const { isMobile, isKeyboardOpen } = useDeviceCapabilities();

  const {
    authMode,
    emailStatus,
    checkedEmail,
    checkEmail,
    resetEmailCheck,
    signup,
    login,
    isAuthLoading,
    authError,
    authErrorCode,
    clearAuthError,
    setEmailStatus,
  } = auth;

  const isDuplicateEmailError = emailStatus === 'new' && authErrorCode === 'duplicate-email';
  const isLegacyResetRequired =
    emailStatus === 'existing' && authErrorCode === 'legacy-reset-required';

  useScrollIntoViewOnReveal(authErrorRef, !!authError, {
    enabled: isMobile,
    block: 'start',
    focus: true,
  });

  // Translated validation schemas
  const emailCheckSchema = useMemo(() => createEmailCheckSchema(tAuth), [tAuth]);
  const signupSchema = useMemo(() => createSignupSchema(tAuth), [tAuth]);
  const loginSchema = useMemo(() => createLoginSchema(tAuth), [tAuth]);

  // Fetch countries for phone input
  const { data: countries = [] } = useCountries();

  // Vehicle type from prop, falling back to tenant-slug derivation (DGD fetches cars)
  const tenantSlug = useTenantSlug();
  const isDgd = tenantSlug === 'dgd';
  const vehicleType: 'motorcycle' | 'car' = vehicleTypeProp ?? (isDgd ? 'car' : 'motorcycle');
  const isCar = vehicleType === 'car';
  const vehicleLabel = isCar ? tAuth('yourVehicle') : tAuth('yourRide');
  const VehicleIcon = isCar ? Car : Bike;

  // Email check form
  const emailForm = useForm<EmailCheckFormData>({
    resolver: zodResolver(emailCheckSchema),
    defaultValues: {
      email: checkedEmail ?? '',
    },
  });

  // Signup form — email pre-filled from checkedEmail
  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: checkedEmail ?? '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      vehicleYear: '',
      vehicleMake: '',
      vehicleModel: '',
    },
  });

  // Pre-select the campaign's default vehicle make once, without clobbering a
  // make the rider has already chosen.
  const defaultVehicleMake = useCampaignDefaultMake(eventId);
  const seededMakeRef = useRef(false);
  useEffect(() => {
    if (seededMakeRef.current || !defaultVehicleMake) return;
    if (!signupForm.getValues('vehicleMake')) {
      signupForm.setValue('vehicleMake', defaultVehicleMake);
    }
    seededMakeRef.current = true;
  }, [defaultVehicleMake, signupForm]);

  // DGD vehicle eligibility — cars must be 40+ years old.
  // Show a slide-out warning when the entered year is too recent.
  const watchedVehicleYear = useWatch({ control: signupForm.control, name: 'vehicleYear' });
  const showEligibilityWarning = useMemo(() => {
    if (!isDgd) return false;
    const yearNum = Number(watchedVehicleYear);
    if (!Number.isFinite(yearNum) || yearNum <= 0) return false;
    const currentYear = new Date().getFullYear();
    return currentYear - yearNum < 40;
  }, [isDgd, watchedVehicleYear]);

  // Login form — email pre-filled from checkedEmail
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: checkedEmail ?? '',
      password: '',
    },
  });
  const { ref: loginPasswordFieldRef, ...loginPasswordField } = loginForm.register('password');

  // ──────────────────────────────────────────────────
  // Handlers
  // ──────────────────────────────────────────────────

  async function handleEmailCheck(data: EmailCheckFormData) {
    await checkEmail(data.email, eventId);
    // Pre-fill forms with checked email
    signupForm.setValue('email', data.email);
    loginForm.setValue('email', data.email);
  }

  function handleChangeEmail() {
    clearAuthError();
    resetEmailCheck();
    emailForm.reset({ email: checkedEmail ?? '' });
  }

  async function handleSignup(data: SignupFormData) {
    clearAuthError();
    const result = await signup({
      ...data,
      defaultBlog: {
        title: tRegister('defaultBlogTitle'),
        content: tRegister('defaultBlogContent'),
      },
      eventId,
      joinWaitlist,
    });
    if (result.success) {
      if (auth.entrant?.userId) identify(auth.entrant.userId);
      trackRego('rego_auth_completed', { auth_mode: 'signup' });
      onSuccess();
    }
  }

  async function handleLogin(data: LoginFormData) {
    clearAuthError();
    const result = await login({
      ...data,
      defaultBlog: {
        title: tRegister('defaultBlogTitle'),
        content: tRegister('defaultBlogContent'),
      },
      eventId,
      joinWaitlist,
    });
    if (result.success) {
      if (auth.entrant?.userId) identify(auth.entrant.userId);
      trackRego('rego_auth_completed', { auth_mode: 'login' });
      onSuccess();
    }
  }

  function handleRegisterNewRider() {
    fetch('/api/auth/sign-out', {
      method: 'POST',
      credentials: 'include',
    }).then(() => {
      window.location.href = '/register';
    });
  }

  function handleDuplicateEmailAction() {
    // Race condition: signup said "new" but account was created since.
    // Switch to login flow.
    trackRego('rego_email_checked', {
      result: 'existing',
      is_retry: true,
      is_duplicate_switch: true,
    });
    clearAuthError();
    loginForm.setValue('email', checkedEmail ?? '', {
      shouldDirty: true,
      shouldTouch: true,
    });
    loginForm.setValue('password', '');
    loginForm.clearErrors();
    // Force emailStatus to 'existing' so login form shows
    // autoFocus on the password field handles focus when the form mounts
    setEmailStatus('existing');
  }

  // ──────────────────────────────────────────────────
  // Complete registration fallback (auth-without-entrant)
  // ──────────────────────────────────────────────────

  if (authMode === 'complete-registration' || authenticatedProfile) {
    return (
      <CompleteRegistrationStep
        onSuccess={onSuccess}
        onBack={onBack}
        cityName={cityName}
        className={className}
        eventId={eventId}
        joinWaitlist={joinWaitlist}
        auth={auth}
        prefilledProfileOverride={authenticatedProfile}
        vehicleType={vehicleTypeProp}
        theme={theme}
      />
    );
  }

  // ──────────────────────────────────────────────────
  // Heading
  // ──────────────────────────────────────────────────

  const isExistingUser = emailStatus === 'existing';
  const isExistingRegisteredUser = emailStatus === 'existing-registered';

  const heading =
    emailStatus === 'unchecked' || emailStatus === 'checking'
      ? tAuth('enterEmail')
      : emailStatus === 'new'
        ? tAuth('createAccount')
        : tAuth('welcomeBack');

  return (
    <div className={className}>
      <h1 className={`text-3xl md:text-4xl font-bold ${c.heading} mb-2`}>{heading}</h1>
      {cityName && (
        <p className={`${c.subtitle} mb-8`}>
          {tAuth.rich('registeringFor', {
            cityName,
            highlight: (chunks: React.ReactNode) => (
              <span className={`${c.highlight} font-medium`}>{chunks}</span>
            ),
          })}
        </p>
      )}

      {/* Error Alert */}
      {authError && (
        <div
          ref={authErrorRef}
          tabIndex={-1}
          role="alert"
          aria-live="assertive"
          className={c.errorBox}
        >
          <AlertCircle className={c.errorIcon} aria-hidden="true" />
          <div>
            <p className={c.errorTitle}>
              {isDuplicateEmailError
                ? tAuth('emailAlreadyExistsTitle')
                : isLegacyResetRequired
                  ? tAuth('legacyPasswordResetRequiredTitle')
                  : emailStatus === 'new'
                    ? tAuth('signupFailed')
                    : tAuth('loginFailed')}
            </p>
            <p className={c.errorBody}>
              {isDuplicateEmailError
                ? tAuth('emailAlreadyExistsMessage')
                : isLegacyResetRequired
                  ? tAuth('legacyPasswordResetRequiredMessage')
                  : authError}
            </p>
            {isDuplicateEmailError && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={handleDuplicateEmailAction}
                  className={c.errorPrimaryButton}
                >
                  {tAuth('loginButton')}
                </Button>
                <Button type="button" variant="ghost" asChild className={c.errorGhostButton}>
                  <Link
                    href="/forgot-password"
                    onClick={() => {
                      trackRego('rego_login_fallback', { from: 'forgot_password' });
                    }}
                  >
                    {tAuth('forgotPasswordLink')}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          Phase 1: Email Entry
          ═══════════════════════════════════════════════ */}
      {(emailStatus === 'unchecked' || emailStatus === 'checking') && (
        <form
          id="registration-auth-email-form"
          onSubmit={emailForm.handleSubmit(handleEmailCheck)}
          className="space-y-5 pb-32 md:pb-0"
          autoComplete="on"
        >
          <p className={c.formDescription}>{tAuth('enterEmailDescription')}</p>
          <div className="space-y-2">
            <Label htmlFor="check-email" className={c.label}>
              {tAuth('emailAddress')}
            </Label>
            <Input
              id="check-email"
              type="email"
              {...emailForm.register('email')}
              className={c.input}
              placeholder={tAuth('emailPlaceholder')}
              autoComplete="email"
              autoFocus
              aria-describedby={emailForm.formState.errors.email ? 'check-email-error' : undefined}
              aria-invalid={!!emailForm.formState.errors.email}
            />
            {emailForm.formState.errors.email && (
              <p id="check-email-error" role="alert" className={c.fieldError}>
                {emailForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <StepFormActions
            stepNumber={2}
            stepLabel={tRegister('yourDetails')}
            submitFormId="registration-auth-email-form"
            submitLabel={tAuth('continueButton')}
            loadingLabel={tAuth('checkingEmail')}
            isLoading={emailStatus === 'checking'}
            showBack={!!onBack}
            onBack={onBack}
            backLabel={tCommon('back')}
            isKeyboardOpen={isKeyboardOpen}
            theme={theme}
          />
        </form>
      )}

      {/* ═══════════════════════════════════════════════
          Phase 2a: New User — Signup Form
          ═══════════════════════════════════════════════ */}
      {emailStatus === 'new' && (
        <>
          <EmailBadge
            email={checkedEmail ?? ''}
            onChangeEmail={handleChangeEmail}
            changeLabel={tAuth('changeEmail')}
            theme={theme}
          />
          <form
            id="registration-auth-signup-form"
            onSubmit={signupForm.handleSubmit(handleSignup)}
            className="space-y-5 pb-32 md:pb-0"
            autoComplete="on"
          >
            {/* Hidden email for password managers */}
            <input type="hidden" {...signupForm.register('email')} />

            {/* Name */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signup-firstName" className={c.label}>
                  {tAuth('firstName')}
                </Label>
                <Input
                  id="signup-firstName"
                  {...signupForm.register('firstName')}
                  className={c.input}
                  placeholder={tAuth('firstNamePlaceholder')}
                  autoComplete="given-name"
                  autoFocus
                  aria-describedby={
                    signupForm.formState.errors.firstName ? 'signup-firstName-error' : undefined
                  }
                  aria-invalid={!!signupForm.formState.errors.firstName}
                />
                {signupForm.formState.errors.firstName && (
                  <p id="signup-firstName-error" role="alert" className={c.fieldError}>
                    {signupForm.formState.errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-lastName" className={c.label}>
                  {tAuth('lastName')}
                </Label>
                <Input
                  id="signup-lastName"
                  {...signupForm.register('lastName')}
                  className={c.input}
                  placeholder={tAuth('lastNamePlaceholder')}
                  autoComplete="family-name"
                  aria-describedby={
                    signupForm.formState.errors.lastName ? 'signup-lastName-error' : undefined
                  }
                  aria-invalid={!!signupForm.formState.errors.lastName}
                />
                {signupForm.formState.errors.lastName && (
                  <p id="signup-lastName-error" role="alert" className={c.fieldError}>
                    {signupForm.formState.errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="signup-phone" className={c.label}>
                {tAuth('phone')} <span className={c.requiredMark}>*</span>
              </Label>
              <Controller
                name="phone"
                control={signupForm.control}
                render={({ field }) => (
                  <PhoneInput
                    id="signup-phone"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    defaultCountry={countryCode || 'AU'}
                    countries={countries}
                    variant={REGISTRATION_PHONE_VARIANT[theme]}
                    size="lg"
                  />
                )}
              />
              {signupForm.formState.errors.phone && (
                <p id="signup-phone-error" role="alert" className={c.fieldError}>
                  {signupForm.formState.errors.phone.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="signup-password" className={c.label}>
                {tAuth('createPassword')}
              </Label>
              <PasswordInput
                id="signup-password"
                {...signupForm.register('password')}
                className={c.input}
                placeholder={tAuth('minChars')}
                autoComplete="new-password"
                aria-describedby={
                  signupForm.formState.errors.password
                    ? 'signup-password-error'
                    : 'signup-password-hint'
                }
                aria-invalid={!!signupForm.formState.errors.password}
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              {signupForm.formState.errors.password ? (
                <p id="signup-password-error" role="alert" className={c.fieldError}>
                  {signupForm.formState.errors.password.message}
                </p>
              ) : (
                <p id="signup-password-hint" className={c.passwordHint}>
                  {tAuth('validation.minLength')}
                </p>
              )}
            </div>

            {/* Motorcycle Section Divider */}
            <div className="flex items-center gap-3 pt-4">
              <div className={c.dividerLine} />
              <span className={c.dividerLabel}>
                <VehicleIcon className="w-4 h-4" />
                {vehicleLabel}
              </span>
              <div className={c.dividerLine} />
            </div>

            {/* Year, Make, Model */}
            <VehicleFields
              control={signupForm.control}
              theme={theme}
              vehicleType={vehicleType}
              idPrefix="signup"
            />

            {/* DGD vehicle eligibility callout — slides out when year < 40 yrs old */}
            <div
              className={`grid transition-all duration-300 ease-out ${
                showEligibilityWarning ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
              aria-hidden={!showEligibilityWarning}
            >
              <div className="overflow-hidden">
                <div className="mt-2 flex gap-3 rounded-lg border border-dgd-gold/40 bg-dgd-cream/95 p-4 text-dgd-charcoal">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-dgd-tan" />
                  <div className="space-y-1.5">
                    <p className="font-semibold text-sm">{tAuth('eligibilityCheckTitle')}</p>
                    <p className="text-sm leading-relaxed">
                      {tAuth('eligibilityCheckBodyDgd')}{' '}
                      <Link
                        href="/about/vehicle-eligibility"
                        className="font-semibold underline underline-offset-2 hover:text-dgd-tan"
                      >
                        {tAuth('eligibilityCheckLink')}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <StepFormActions
              stepNumber={2}
              stepLabel={tRegister('yourDetails')}
              submitFormId="registration-auth-signup-form"
              submitLabel={tAuth('createAccountButton')}
              loadingLabel={tAuth('creatingAccount')}
              isLoading={isAuthLoading}
              showBack={!!onBack}
              onBack={onBack}
              backLabel={tCommon('back')}
              isKeyboardOpen={isKeyboardOpen}
              theme={theme}
            />
          </form>
        </>
      )}

      {/* ═══════════════════════════════════════════════
          Phase 2b: Existing User — Login Form
          ═══════════════════════════════════════════════ */}
      {isExistingUser && (
        <>
          <EmailBadge
            email={checkedEmail ?? ''}
            onChangeEmail={handleChangeEmail}
            changeLabel={tAuth('changeEmail')}
            theme={theme}
          />
          <form
            id="registration-auth-login-form"
            onSubmit={loginForm.handleSubmit(handleLogin)}
            className="space-y-5 pb-32 md:pb-0"
            autoComplete="on"
          >
            {/* Hidden email for password managers */}
            <input type="hidden" {...loginForm.register('email')} />

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="login-password" className={c.label}>
                {tAuth('password')}
              </Label>
              <PasswordInput
                id="login-password"
                ref={loginPasswordFieldRef}
                {...loginPasswordField}
                className={c.input}
                placeholder={tAuth('passwordPlaceholder')}
                autoComplete="current-password"
                autoFocus
                aria-describedby={
                  loginForm.formState.errors.password ? 'login-password-error' : undefined
                }
                aria-invalid={!!loginForm.formState.errors.password}
              />
              {loginForm.formState.errors.password && (
                <p id="login-password-error" role="alert" className={c.fieldError}>
                  {loginForm.formState.errors.password.message}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                id="forgot-password-link"
                href="/forgot-password"
                className={c.forgotLink}
                onClick={() => {
                  trackRego('rego_login_fallback', { from: 'forgot_password' });
                }}
              >
                {tAuth('forgotPasswordLink')}
              </Link>
            </div>

            {/* Info about returning users */}
            <div className={c.noticeBox}>
              <p className={c.noticeText}>{tAuth('previousRegistrationNotice')}</p>
            </div>

            {/* Actions */}
            <StepFormActions
              stepNumber={2}
              stepLabel={tRegister('yourDetails')}
              submitFormId="registration-auth-login-form"
              submitLabel={tAuth('loginButton')}
              loadingLabel={tAuth('loggingIn')}
              isLoading={isAuthLoading}
              showBack={!!onBack}
              onBack={onBack}
              backLabel={tCommon('back')}
              isKeyboardOpen={isKeyboardOpen}
              theme={theme}
            />
          </form>
        </>
      )}

      {isExistingRegisteredUser && (
        <>
          <EmailBadge
            email={checkedEmail ?? ''}
            onChangeEmail={handleChangeEmail}
            changeLabel={tAuth('changeEmail')}
            theme={theme}
          />

          <div className="space-y-5 pb-32 md:pb-0">
            <div className={c.successBox}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className={c.successIcon} />
                <div>
                  <p className={c.successTitle}>{tRegister('alreadyRegisteredMessage')}</p>
                  <p className={c.successBody}>{tRegister('alreadyRegisteredChangeRide')}</p>
                </div>
              </div>
            </div>

            <StepFormActions
              stepNumber={2}
              stepLabel={tRegister('yourDetails')}
              submitFormId="registration-auth-blocked-form"
              submitLabel={tRegister('goToDashboard')}
              loadingLabel={tRegister('goToDashboard')}
              isLoading={false}
              submitHref="/my/dashboard"
              showBack
              onBack={handleRegisterNewRider}
              backLabel={tRegister('registerNewRider')}
              backVariant="outline"
              isKeyboardOpen={isKeyboardOpen}
              theme={theme}
            />
          </div>
        </>
      )}
    </div>
  );
}
